/**
 * A testing preview's clock, as the test console and the preview banner show it
 * (docs/features/quote-to-invoice-e2e-testing/07 §1, §3): the preview's time and how far it has been moved on.
 */

import { useEffect, useState } from "react";
import type { DataAdapter } from "../data/adapter";
import type { PreviewClock } from "../data/journey-testing-types";

/** "Tue 14 Oct 09:12". */
export function formatPreviewTime(isoValue: string): string {
  return new Date(isoValue).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** How far the preview has been moved on: "+7 days", "+1 day 3 hours", or "real time". */
export function formatClockOffset(offsetSeconds: number): string {
  if (offsetSeconds <= 0) return "real time";
  const days = Math.floor(offsetSeconds / 86_400);
  const hours = Math.floor((offsetSeconds % 86_400) / 3_600);
  const parts = [
    days ? `${days} day${days === 1 ? "" : "s"}` : "",
    hours ? `${hours} hour${hours === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  return `+${parts.join(" ") || "under an hour"}`;
}

/**
 * The preview clock, re-read every minute while `enabled` (a testing preview). Null until the first read, or when
 * the read fails (the banner then simply leaves the clock out).
 */
export function usePreviewClock(
  adapter: Pick<DataAdapter, "getPreviewClock">,
  enabled: boolean,
  refreshMs = 60_000,
): PreviewClock | null {
  const [clock, setClock] = useState<PreviewClock | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const read = () =>
      adapter
        .getPreviewClock()
        .then((value) => !cancelled && setClock(value))
        .catch(() => !cancelled && setClock(null));
    void read();
    const timer = setInterval(read, refreshMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [adapter, enabled, refreshMs]);
  return clock;
}
